import React, { useState } from 'react';
import { Alert, Modal, Upload, Select, Spin, message } from 'antd';
import { InboxOutlined } from '@ant-design/icons';
import { getPartitionUploadUrl, getLyricsUploadUrl, addPartition, addLyrics } from '../Feature/API';
import { uploadFile } from '../Feature/requests';
import { PARTITION_FORMATS, getPartitionUploadHint } from '../Utils/songPartitions';

export const FileUploadModal = ({ visible, onClose, song, spaceId, onFileUpload }) => {
    const [uploadType, setUploadType] = useState('partition');
    const [loading, setLoading] = useState(false);
    const [file, setFile] = useState(null);

    const handleUpload = async () => {
        if (!file) {
            message.error('Please select a file');
            return;
        }

        setLoading(true);
        try {
            if (uploadType === 'partition') {
                await uploadPartition(file);
            } else {
                await uploadLyrics(file);
            }
            message.success(`${uploadType === 'partition' ? 'Partition' : 'Lyrics'} uploaded successfully`);
            onFileUpload();
            handleClose();
        } catch (error) {
            message.error('Failed to upload: ' + (error.message || 'Unknown error'));
        } finally {
            setLoading(false);
        }
    };

    const uploadPartition = async (partitionFile) => {
        if (!PARTITION_FORMATS.some(fmt => partitionFile.name.toLowerCase().endsWith('.' + fmt))) {
            throw new Error(`Unsupported format. Supported: ${PARTITION_FORMATS.join(', ')}`);
        }

        const ext = partitionFile.name.split('.').pop();
        const presignedResponse = await getPartitionUploadUrl(spaceId, song.id, partitionFile.name, ext.toLowerCase());
        await uploadFile(presignedResponse.url, partitionFile);
        await addPartition(spaceId, song.id, partitionFile.name, ext.toLowerCase());
    };

    const uploadLyrics = async (lyricsFile) => {
        const presignedResponse = await getLyricsUploadUrl(spaceId, song.id, lyricsFile.name);
        await uploadFile(presignedResponse.url, lyricsFile);
        await addLyrics(spaceId, song.id, lyricsFile.name, null);
    };

    const handleClose = () => {
        setFile(null);
        setUploadType('partition');
        onClose();
    };

    return (
        <Modal
            title="Upload File"
            open={visible}
            onCancel={handleClose}
            onOk={handleUpload}
            confirmLoading={loading}
            width={600}
        >
            <Spin spinning={loading}>
                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                        File Type
                    </label>
                    <Select
                        value={uploadType}
                        onChange={setUploadType}
                        options={[
                            { label: 'Partition', value: 'partition' },
                            { label: 'Lyrics', value: 'lyrics' },
                        ]}
                        style={{ width: '100%' }}
                    />
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: 500 }}>
                        Select File
                    </label>
                    <Upload.Dragger
                        accept={uploadType === 'partition' ? PARTITION_FORMATS.map(f => `.${f}`).join(',') : '*'}
                        beforeUpload={(uploadFile) => {
                            setFile(uploadFile);
                            return false;
                        }}
                        maxCount={1}
                        fileList={file ? [file] : []}
                    >
                        <p className="ant-upload-drag-icon">
                            <InboxOutlined />
                        </p>
                        <p className="ant-upload-text">Click or drag file to upload</p>
                        {uploadType === 'partition' && (
                            <p className="ant-upload-hint">
                                Supported formats: {PARTITION_FORMATS.join(', ')}
                            </p>
                        )}
                    </Upload.Dragger>
                </div>

                {uploadType === 'partition' && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: '20px' }}
                        message={file ? getPartitionUploadHint(file.name) : 'Upload any partition file. Guitar Pro sources are additionally parsed so they can be downloaded later as PDF or GP.'}
                    />
                )}

                {file && (
                    <div style={{
                        padding: '12px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '4px',
                        marginBottom: '20px'
                    }}>
                        <strong>Selected:</strong> {file.name}
                    </div>
                )}
            </Spin>
        </Modal>
    );
};
